import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Clock, CloudUpload, Phone, Mail, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { Accordion, Paragraphs, useSingleOpen } from "@/components/ContentParts";
import { inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

export const fileToBase64 = (file) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(",")[1]); fr.onerror = rej; fr.readAsDataURL(file); });

export function Warranty() {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, toggle] = useSingleOpen();
  useEffect(() => { api("/warranty").then(setDoc).catch((e) => setErr(e.message)); }, []);
  return (
    <PortalPage eyebrow={doc?.effective_date ? `EFFECTIVE ${doc.effective_date}` : "WARRANTY"} title={doc?.title || "ALR Limited Warranty Policy"} testId="warranty-page">
      <ErrorNote>{err}</ErrorNote>
      {!doc && !err && <Spinner />}
      {doc?.sections.map((s) => (
        <Accordion key={s.id} id={`warranty-${s.id}`} eyebrow={`SECTION ${s.id}`} title={s.title} open={openId === s.id} onToggle={() => toggle(s.id)}>
          <Paragraphs text={s.body} />
          {s.bullets && <ul className="mt-2 list-disc space-y-1 pl-5">{s.bullets.map((b) => <li key={b}>{b}</li>)}</ul>}
        </Accordion>
      ))}
    </PortalPage>
  );
}

const STATE = {
  ok: [ShieldCheck, "var(--accent)", "Policy on file"],
  expiring: [Clock, "#F5A623", "Policy expiring soon"],
  needs_update: [ShieldAlert, "#F5A623", "Policy expired — upload your renewal"],
  new_grace: [Hourglass, "#F5A623", "Upload your insurance certificate"],
  pending_review: [Hourglass, "#3B82F6", "Policy submitted — awaiting ALR review"],
  default: [ShieldAlert, "#FF3333", "Account in default — insurance required"],
  not_required: [ShieldCheck, "#888", "Insurance not required for this account"],
};

const NextStep = () => {
  const navigate = useNavigate();
  const [lso, setLso] = useState(null);
  useEffect(() => { api("/lso/status/me").then(setLso).catch(() => setLso({})); }, []);
  if (!lso) return null;
  const train = lso.locked && lso.self_unlock;
  return (
    <div className="mt-3 space-y-2">
      <p className="text-sm text-neutral-300" data-testid="insurance-next-text">{train ? "Thanks! Next step: finish your LSO Training. Your account unlocks automatically when you pass." : "Thanks! You can keep using the portal while ALR reviews your policy."}</p>
      <Btn onClick={() => navigate(train ? "/lso" : "/dashboard")} data-testid="insurance-next-btn">{train ? "Start LSO Training" : "Back to dashboard"}</Btn>
    </div>
  );
};

export function Insurance() {
  const fileRef = useRef(null);
  const [s, setS] = useState(null);
  const [file, setFile] = useState(null);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api("/insurance/status").then(setS).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return setErr("Choose your certificate of insurance (PDF, JPG or PNG).");
    if (!expires) return setErr("Enter the policy expiration date.");
    setBusy(true); setErr(null);
    try {
      setS(await api("/insurance/upload", { method: "POST", body: { file_b64: await fileToBase64(file), filename: file.name, expires_at: expires } }));
      setFile(null); setExpires("");
      toast.success("Uploaded — ALR will review your policy.");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const [Icon, color, label] = STATE[s?.status] || STATE.ok;
  const days = s?.days_remaining;
  return (
    <PortalPage eyebrow="FRANCHISE REQUIREMENT" title="Insurance" testId="insurance-page">
      {!s && !err && <Spinner />}
      {s && (
        <Card style={{ borderColor: color }} data-testid="insurance-status">
          <Icon size={30} style={{ color }} />
          <p className="mt-2 font-display text-2xl font-bold uppercase" data-testid="insurance-status-label">{label}</p>
          <div className="mt-2 space-y-1 text-sm text-neutral-400">
            {s.filename && <p>File: <span className="text-white">{s.filename}</span></p>}
            {s.expires_at && <p>Expires: <span className="text-white">{fmtDate(s.expires_at)}</span>{days != null && s.status !== "new_grace" ? ` (${days} days)` : ""}</p>}
            {s.status === "new_grace" && s.days_until_default != null && <p>You have <b className="text-white">{s.days_until_default} days</b> to upload before your account goes into default.</p>}
            {s.status === "pending_review" && <p>Submitted {fmtDate(s.pending_uploaded_at)}: {s.pending_filename} (expires {fmtDate(s.pending_expires_at)})</p>}
          </div>
          {s.status === "pending_review" && <NextStep />}
        </Card>
      )}
      {s && s.status !== "not_required" && (
        <Card className="space-y-3" data-testid="insurance-upload">
          <p className="font-bold">{s.has_policy ? "Upload a renewed policy" : "Upload your certificate of insurance"}</p>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="insurance-file-input" />
          <Btn variant="outline" onClick={() => fileRef.current?.click()} data-testid="insurance-choose-file"><CloudUpload size={16} /> {file ? file.name : "Choose file"}</Btn>
          <label className="block text-xs font-bold tracking-[0.2em] text-neutral-400">POLICY EXPIRATION DATE<input type="date" data-testid="insurance-expires" className={`${inputCls} mt-1.5`} value={expires} onChange={(e) => setExpires(e.target.value)} /></label>
          <ErrorNote>{err}</ErrorNote>
          <Btn busy={busy} onClick={upload} data-testid="insurance-submit">Submit for review</Btn>
        </Card>
      )}
      {s && (
        <Card className="space-y-1 text-sm">
          <p className="text-[11px] font-bold tracking-[0.3em] text-neutral-400">QUESTIONS?</p>
          <a href={`tel:${s.corporate_phone}`} className="flex items-center gap-2 text-[var(--accent)]"><Phone size={14} />{s.corporate_phone}</a>
          <a href={`mailto:${s.corporate_email}`} className="flex items-center gap-2 text-[var(--accent)]"><Mail size={14} />{s.corporate_email}</a>
        </Card>
      )}
    </PortalPage>
  );
}
